import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Smile, Frown, Users, Heart, MessageSquare, Download, TrendingUp } from "lucide-react";
import { toast } from "sonner";

const todayStr = () => new Date().toISOString().slice(0, 10);

export default function MoodDashboard() {
  const [date, setDate] = useState(todayStr());
  const [data, setData] = useState(null);
  const [weekly, setWeekly] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: today }, { data: trend }] = await Promise.all([
        api.get(`/mood/dashboard?date=${date}`),
        api.get(`/mood/weekly?days=7`),
      ]);
      setData(today);
      setWeekly(trend);
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => { load(); }, [load]);

  // Sort once per data change — unhappy first
  const sortedItems = useMemo(
    () => (data?.items ? [...data.items].sort((a, b) => Number(a.happy) - Number(b.happy)) : []),
    [data]
  );

  const exportCsv = () => {
    if (!weekly?.series?.length) {
      toast.error("No data to export");
      return;
    }
    const header = ["Date", "Happy", "Unhappy", "Responses", "Happiness %"].join(",");
    const rows = weekly.series.map((d) => [d.date, d.happy, d.unhappy, d.responses, d.happiness_pct].join(","));
    const csv = [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `mood-weekly-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("CSV downloaded");
  };

  const maxPct = useMemo(() => Math.max(1, ...((weekly?.series || []).map((d) => d.happiness_pct))), [weekly]);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <span className="label-overline">Wellbeing</span>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-1 inline-flex items-center gap-3">
            <Heart className="w-8 h-8 text-rose-500" strokeWidth={1.5} /> Team Mood
          </h1>
          <p className="text-sm text-zinc-500 mt-2">Daily check-ins from your team. Unhappy team members are pinned to the top so you can reach out today.</p>
        </div>
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="text-sm border border-zinc-300 rounded-md px-3 py-2"
          data-testid="mood-dashboard-date"
        />
      </div>

      {/* Weekly trendline */}
      {!loading && weekly && (
        <div className="bg-white border border-zinc-200 rounded-md p-5" data-testid="mood-trendline">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
            <div>
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-zinc-700" />
                <h3 className="font-display text-xl font-bold tracking-tight">7-day happiness trend</h3>
              </div>
              <p className="text-xs text-zinc-500 mt-0.5">Day-by-day percentage of happy check-ins across your scope.</p>
            </div>
            <button
              onClick={exportCsv}
              className="text-xs uppercase tracking-widest font-semibold px-3 py-1.5 border border-zinc-300 rounded hover:bg-zinc-50 inline-flex items-center gap-1.5"
              data-testid="mood-csv-export"
            >
              <Download className="w-3.5 h-3.5" /> Export CSV
            </button>
          </div>
          <div className="flex items-end gap-2 h-32" data-testid="mood-trendline-bars">
            {(weekly.series || []).map((d) => (
              <div key={d.date} className="flex-1 flex flex-col items-center justify-end gap-1.5 group">
                <div
                  className={`w-full rounded-t transition ${d.happiness_pct >= 70 ? "bg-emerald-400" : d.happiness_pct >= 40 ? "bg-amber-400" : "bg-rose-400"}`}
                  style={{ height: `${Math.max(4, (d.happiness_pct / maxPct) * 100)}%` }}
                  title={`${d.date} — ${d.happiness_pct}% happy (${d.responses} responses)`}
                />
                <div className="text-[10px] text-zinc-500 group-hover:text-zinc-900 font-mono">{d.date.slice(5)}</div>
                <div className="text-[11px] font-semibold text-zinc-700 group-hover:text-zinc-900">{d.happiness_pct}%</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading || !data ? (
        <div className="text-zinc-500 text-sm">Loading…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Eligible team" value={data.eligible} icon={Users} accent="zinc" testid="stat-eligible" />
            <Stat label="Happy" value={data.happy_count} icon={Smile} accent="emerald" testid="stat-happy" />
            <Stat label="Unhappy" value={data.unhappy_count} icon={Frown} accent="orange" testid="stat-unhappy" />
            <Stat label="Pending check-in" value={data.pending_count} icon={MessageSquare} accent="zinc" testid="stat-pending" />
          </div>

          <div className="bg-white border border-zinc-200 rounded-md p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="label-overline">Happiness index</span>
              <span className="font-mono text-2xl font-black tracking-tighter">{data.happiness_pct}%</span>
            </div>
            <div className="h-3 bg-zinc-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600 transition-all"
                style={{ width: `${data.happiness_pct}%` }}
                data-testid="happiness-bar"
              />
            </div>
            <div className="text-[11px] text-zinc-500 mt-2">
              Based on {data.happy_count + data.unhappy_count} of {data.eligible} eligible check-ins today.
            </div>
          </div>

          <div className="space-y-3">
            <h3 className="font-display text-lg font-bold tracking-tight">Today&apos;s responses</h3>
            {data.items.length === 0 ? (
              <div className="bg-white border border-zinc-200 rounded-md p-8 text-center text-zinc-400">
                No mood check-ins for this date yet.
              </div>
            ) : (
              <div className="space-y-2">
                {/* Unhappy first */}
                {sortedItems.map((m) => (
                  <MoodRow key={m.id} m={m} />
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function MoodRow({ m }) {
  const happy = m.happy;
  return (
    <div
      className={`border-l-4 rounded-md p-4 ${happy ? "border-emerald-500 bg-emerald-50" : "border-orange-500 bg-orange-50"}`}
      data-testid={`mood-row-${m.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          {happy ? (
            <Smile className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
          ) : (
            <Frown className="w-5 h-5 text-orange-600 shrink-0 mt-0.5" />
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold">{m.user_name}</span>
              <span className="text-[10px] text-zinc-500 uppercase tracking-widest">{(m.user_role || "").replace("_", " ")}</span>
            </div>
            <div className="mt-1 text-sm text-zinc-800 whitespace-pre-wrap">{m.reason}</div>
          </div>
        </div>
        <span className="text-[11px] text-zinc-500 whitespace-nowrap">{new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
      </div>
      {m.affirmation_text && (
        <div className="mt-3 text-[12px] italic text-zinc-600 border-t border-zinc-200 pt-2">
          Management replied: {m.affirmation_text}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, icon: Icon, accent, testid }) {
  const palette = {
    emerald: "text-emerald-700",
    orange: "text-orange-700",
    zinc: "text-zinc-900",
  };
  return (
    <div className="bg-white border border-zinc-200 rounded-md p-4 flex items-start gap-3" data-testid={testid}>
      <Icon className={`w-5 h-5 ${palette[accent] || ""}`} strokeWidth={1.5} />
      <div>
        <div className="label-overline">{label}</div>
        <div className={`font-display text-3xl font-black tracking-tight mt-1 ${palette[accent] || ""}`}>{value}</div>
      </div>
    </div>
  );
}
